import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyIcon from "@mui/icons-material/Key";
import LogoutIcon from "@mui/icons-material/Logout";
import type { SimplePaletteColorOptions } from "@mui/material";
import {
  Button,
  Container,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { getUser } from "../../api/user";
import { themeOptions } from "../../app/theme";
import { TextMaskCustom } from "../../components/DocumentTextField";
import Loading from "../../components/Loading";
import getUserLocal from "../../utils/getUser";

TextMaskCustom.displayName = "TextMaskCustom";

function MyAccountPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = getUserLocal()?.id as number | undefined;
  const { data, error } = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => await getUser(userId as number),
    enabled: Boolean(userId),
    retry: false,
  });

  useEffect(() => {
    if (error) {
      navigate({ to: "/error" });
    }
  }, [error, navigate]);
  const name = data?.name || "";
  const document = data?.document || "";
  const errorColor = themeOptions.palette?.error as SimplePaletteColorOptions;
  const [openModalExit, setOpenModalExit] = useState(false);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Container
        sx={{
          flexGrow: 1,
        }}
      >
        <Box className="container">
          <Typography
            component="h1"
            sx={{
              fontSize: {
                xs: "24px",
                sm: "24px",
                md: "32px",
                lg: "32px",
                xl: "32px",
              },
              fontWeight: 500,
            }}
          >
            {t("myAccount.title")}
          </Typography>
          <Box
            gap={2}
            sx={{ width: "100%", display: "flex", flexDirection: "column" }}
          >
            <TextField
              color="primary"
              disabled
              fullWidth
              label={t("myAccount.fullNameLabel")}
              size="small"
              sx={{
                "& .MuiFormLabel-root": {
                  color: "text.disabled",
                },
                "& .MuiInputBase-input": {
                  color: "primary.main",
                },
              }}
              value={name}
              variant="outlined"
            />
            <TextField
              InputProps={{
                inputComponent: TextMaskCustom as never,
                inputProps: {
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                  mask: "000.000.000-00",
                },
              }}
              color="primary"
              disabled
              fullWidth
              label={t("myAccount.cpfLabel")}
              size="small"
              sx={{
                "& .MuiFormLabel-root": {
                  color: "text.disabled",
                },
                "& .MuiInputBase-input": {
                  color: "primary.main",
                },
              }}
              value={document}
              variant="outlined"
            />
          </Box>
          <Box sx={{ width: "100%" }}>
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    void navigate({ to: "/edit-password" });
                  }}
                >
                  <KeyIcon
                    color="primary"
                    fontSize="medium"
                    style={{ marginRight: "24px" }}
                  />
                  <ListItemText primary={t("myAccount.editPassword")} />
                  <ChevronRightIcon fontSize="small" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setOpenModalExit(true);
                  }}
                >
                  <ListItemText
                    primary={t("myAccount.logout")}
                    sx={{ color: errorColor.main }}
                  />
                  <LogoutIcon color="error" fontSize="small" />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "flex-start", width: "100%" }}
          >
            <Button
              color="primary"
              component={Link}
              size="medium"
              to="/"
              variant="outlined"
            >
              {t("common.home")}
            </Button>
          </Box>
          <Dialog
            onClose={() => {
              setOpenModalExit(false);
            }}
            open={openModalExit}
          >
            <DialogContent sx={{ padding: "16px" }}>
              <Typography
                component="h2"
                fontFamily="Inter"
                id="spring-modal-title"
                variant="h6"
              >
                {t("myAccount.logoutDialogTitle")}
              </Typography>
              <Typography
                fontFamily="Inter"
                fontWeight={400}
                id="spring-modal-description"
                sx={{ mt: 2 }}
                variant="body1"
              >
                {t("myAccount.logoutDialogMessage")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  gap: "1rem",
                  marginTop: "2rem",
                }}
              >
                <Button
                  color="primary"
                  onClick={() => {
                    void navigate({ to: "/logout" });
                  }}
                  size="medium"
                  variant="contained"
                >
                  {t("myAccount.logoutButton")}
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    setOpenModalExit(false);
                  }}
                  size="medium"
                  variant="outlined"
                >
                  {t("myAccount.stayButton")}
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
        </Box>
      </Container>
      <Stack
        alignItems="center"
        component="footer"
        gap={0.5}
        sx={{
          flexDirection: "column-reverse",
          pb: 2,
        }}
      >
        <Typography color="textSecondary" variant="caption">
          {t("common.smartdocsPortal")}
        </Typography>
        {typeof window.versionSystem === "string" &&
        window.versionSystem.trim() !== "" ? (
          <Typography color="textSecondary" variant="caption">
            {window.versionSystem}
          </Typography>
        ) : (
          <Typography color="textSecondary" variant="caption">
            {t("common.noVersionInfo")}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export const Route = createFileRoute("/__auth/my-account")({
  component: MyAccountPage,
  pendingComponent: Loading,
});
